import { useLoad } from './load'
import { useApiListProviderHealth } from '../../api-new/state/auth'
import { useAuthContext } from '../../context/auth.context'
import { useRoleSysProvider } from '../../role/hook/role-sys'

const MS_PER_DAY = 86400000

/**
 * The soonest-expiring active credential within `windowDays`, or null.
 *
 * Ignores inactive providers (nothing to warn an admin about a connection
 * nobody signs into) and providers with no expiresAt at all (some drivers --
 * facebook, some shared-app connections -- never expire). A row already past
 * its expiry still counts, with a negative daysLeft, since "expired 2 days
 * ago" is exactly the thing this exists to surface.
 */
export const soonestExpiry = (rows, now, windowDays) => {
  let best = null

  for (const row of rows || []) {
    if (!row || !row.active || !row.expiresAt) continue

    const daysLeft = Math.ceil((new Date(row.expiresAt).getTime() - now.getTime()) / MS_PER_DAY)

    if (daysLeft > windowDays) continue

    if (!best || daysLeft < best.daysLeft) {
      best = {
        providerName: row.providerName,
        // Provider-health rows key on providerID (see ProviderHealth's JSON
        // tags in rapid-api-auth); a bare id is accepted too so this stays
        // usable against a plain provider row.
        id: row.providerID ?? row.id,
        daysLeft,
        expiresAt: row.expiresAt
      }
    }
  }

  return best
}

/**
 * Loads the provider fleet once on mount and reports the soonest expiry
 * within a 30-day window, for the sys-provider credential-expiry banner.
 *
 * Only loads when the user is authenticated AND has sys-provider read access,
 * preventing unauthorized API calls for anonymous visitors or org-only users.
 *
 * useApiListProviderHealth() returns a fresh { ...state, ...actions } object
 * every render (api-new hooks have unstable identity -- see app-version.js
 * and the project note on this), so it is never named in an effect's
 * dependency array. useLoad's fixed `[]` deps give the same one-shot-on-mount
 * behaviour app-version.js relies on for its own fetch.
 */
export const useCredentialExpiry = ({ windowDays = 30 } = {}) => {
  const authContext = useAuthContext()
  const roleSysProvider = useRoleSysProvider()
  const apiListProviderHealth = useApiListProviderHealth()

  const shouldLoad = authContext.state.isAuth && roleSysProvider.accessRead

  useLoad(async () => {
    if (shouldLoad) {
      await apiListProviderHealth.get()
    }
  }, [])

  return shouldLoad ? soonestExpiry(apiListProviderHealth.items, new Date(), windowDays) : null
}
